import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  Animated as ReactAnimated,
  FlatList,
  Dimensions,
} from 'react-native';
import Animated, {
  Easing,
  measure,
  runOnUI,
  useAnimatedRef,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { createStackNavigator } from '@react-navigation/stack';
import { isTablet } from 'react-native-device-info';
import { EventRegister } from 'react-native-event-listeners';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import MenuComponent from '../../components/MenuComponent';
import AppContext from '../../navigation/Context';
import * as MenuNavigation from '../../navigation/MenuNavigation';
import colors from '../../resources/colors';
import images from '../../resources/images';
import { useToast } from 'react-native-toast-notifications';
import MenuScreen from '../menu/MenuScreen';
import { render } from 'react-dom';
import { format } from 'date-fns';
import { useNavigation } from '@react-navigation/core';
import DataController from '../../controllers/DataController';
import { FlashList } from '@shopify/flash-list';

const bible = require('../../resources/json/helpers/bibleBooks.json');

const AnimatedKeyboardAwareScrollView = Animated.createAnimatedComponent(KeyboardAwareScrollView);

const Row = ({ row }) => {
  const navigation = useNavigation();

  const buttonPressed = () => {
    navigation.navigate('Book', { book: row.book, sets: row.sets });
  };

  return (
    <View style={{
      width: isTablet() || Platform.OS === 'web'
        ? Dimensions.get('window').width - 60
        : Dimensions.get('window').width - 30,
      maxWidth: 1400,
      alignSelf: 'center',
    }}>
      <TouchableOpacity
        style={styles.browseRow}
        activeOpacity={0.5}
        onPress={buttonPressed}>
        <Text style={styles.book} allowFontScaling={false}>
          {row.book}
        </Text>
        <Image style={styles.icon} source={images.Button_Disclosure_Right_Grey} />
      </TouchableOpacity>
    </View>
  );
};

const renderSeparator = () => (
  <View
    style={[styles.separator, {
      width: isTablet() || Platform.OS === 'web'
        ? Dimensions.get('window').width - 60
        : Dimensions.get('window').width - 30,
    }]}
  />
);

const BrowseBibleScreen = ({ navigation }) => {
  const flatList = useRef(null);
  const toast = useToast();

  const context = useContext(AppContext);
  const { days } = context;

  const [bookmarks, setBookmarks] = useState([]);
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    getBookmarks();
  }, []);

  useEffect(() => {
    getNotes();
  }, []);

  useEffect(() => {
    EventRegister.addEventListener('bookmarks/updated', getBookmarks);
    EventRegister.addEventListener('notes/updated', getNotes);

    return () => {
      EventRegister.removeEventListener('bookmarks/updated');
      EventRegister.removeEventListener('notes/updated');
    }
  }, []);

  const getBookmarks = async () => {
    DataController.getBookmarks().then((_bookmarks) => setBookmarks(_bookmarks));
  };

  const getNotes = async () => {
    DataController.getAllNotes().then((_notes) => setNotes(_notes));
  };

  useEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <View style={styles.headerRow}>
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
            <Image style={styles.menuIcon} source={images.Button_Back} />
            <Text style={styles.backButtonText} allowFontScaling={false}>
              {'Back'}
            </Text>
          </TouchableOpacity>
        </View>
      ),
      headerTitle: () => isTablet() || Platform.OS === 'web' ? (
        <View style={styles.headerTitleContainer}>
          <Text style={styles.headerTitle} allowFontScaling={false}>
            {'Browse by Bible Book'}
          </Text>
        </View>
      ) : <View />,
      headerRight: () => <View />,
      headerTitleAlign: 'center',
      headerStyle: {
        backgroundColor: colors.grey20,
        shadowRadius: 0,
        shadowOffset: {
          height: 0,
        },
        borderBottomWidth: 0.5,
        borderBottomColor: colors.grey50,
        height: Platform.OS === 'web' ? 44 : undefined,
      },
    });
  }, [filterPressed, calendarPressed, navigation]);

  const filterPressed = useCallback(() => {
    toast.show('Not implemented', { id: Date.now() });
  }, [toast]);

  const calendarPressed = useCallback(() => {
    toast.show('Not implemented', { id: Date.now() });
  }, [toast]);

  const renderItem = ({ item, index }) => {
    return <Row row={item} />;
  };

  const keyExtractor = (item, index) => `${item.Date}-${index}`;

  return (
    <View style={styles.container}>
      <FlashList
        ref={flatList}
        data={bible}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        estimatedItemSize={62}
        ItemSeparatorComponent={renderSeparator}
        initialNumToRender={10}
        removeClippedSubviews
        indicatorStyle="black"
        contentContainerStyle={{ paddingBottom: 54 }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.accentWhite,
    flex: 1,
    height: '100%',
  },
  scrollViewContainer: {
    paddingHorizontal: isTablet() || Platform.OS === 'web' ? 30 : 15,
    paddingBottom: 54,
    width: '100%',
    maxWidth: 1400,
    alignSelf: 'center',
  },
  backButton: {
    flexDirection: 'row',
    marginStart: 4,
    alignItems: 'center',
  },
  backButtonText: {
    fontFamily: 'FiraSans-Regular',
    fontSize: 17,
    fontWeight: 'normal',
    color: colors.primary,
    marginStart: -5,
  },
  headerRow: {
    flexDirection: 'row',
  },
  buttonMenu: {
    width: 44,
    height: 44,
    marginStart: 4,
  },
  menuIcon: {
    width: 44,
    height: 44,
    tintColor: colors.primary,
  },
  browseRow: {
    height: 60,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  separator: {
    height: 2,
    backgroundColor: colors.grey30,
    maxWidth: 1400,
    alignSelf: 'center',
  },
  title: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 20,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 28,
    letterSpacing: -0.1,
    color: colors.defaultText,
    marginBottom: 4,
  },
  color: {
    borderRadius: 9,
    backgroundColor: '#009900',
    flex: -1,
    height: 16,
    paddingHorizontal: 6,
    justifyContent: 'center',
    alignSelf: 'center',
  },
  colorText: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 12,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 16,
    letterSpacing: -0.1,
    color: colors.accentWhite,
  },
  caption: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 12,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 16,
    letterSpacing: 0,
    color: colors.defaultText,
  },
  caption2: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 11,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 14,
    letterSpacing: 0.1,
    color: colors.grey60,
    marginTop: 5,
  },
  bottomContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  icon: {
    width: 44,
    height: 44,
    marginStart: 5,
  },
  book: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 24,
    letterSpacing: -0.1,
    color: colors.defaultText,
  },
  headerTitleContainer: {
    maxWidth: 200,
  },
  headerTitle: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    lineHeight: 24,
    letterSpacing: -0.1,
    textAlign: 'center',
    color: colors.primary,
  },
});

export default BrowseBibleScreen;
